import styles from './InlineGallery.module.scss';

import React, { FC, useRef, useState } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import cx from 'classnames';

import { AppType } from 'services/cms/typesUtil';
import { Subject } from 'api/contentful/contentTypes.cloverdotcom';
import { FlatEntry } from 'services/cms/flattenContentful';
import { SubjectMedia } from 'components/SubjectMedia/SubjectMedia';
import { ISwiperClass } from 'components/CarouselModule/CarouselModule';
import { Image } from 'components/Image/Image';
import { useTranslation } from 'react-i18next';
import { Entries } from 'api/contentful/utilTypes';
import { Modal } from 'components/Modal/Modal';

import expandIcon from 'images/icons/arrows-expand-outward.svg';
import closeIcon from 'images/icons/close-light.svg';
import { CircleArrowButton } from 'components/CircleArrowButton/CircleArrowButton';

export type InlineGalleryProps = AppType.Component<
  FlatEntry<{
    items: Entries<Subject>;
    enableModal: boolean;
  }>
>;

export const InlineGalleryModule: FC<InlineGalleryProps> = ({ items = [], enableModal = false }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentIndexModal, setCurrentIndexModal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const apiRef = useRef<{ swiper: ISwiperClass } | null>(null);
  const navRef = useRef<HTMLUListElement | null>(null);
  const { t } = useTranslation();
  const swipperApi = {
    get: () =>
      apiRef.current?.swiper || {
        slideTo: () => {},
        slideNext: () => {},
        slidePrev: () => {},
      },
  };

  if (items.length == 0) return null;

  const swiperProps: SwiperProps = {
    speed: 500,
    slidesPerView: 1,
    watchSlidesProgress: true,
  };

  const scrollBehavior: ScrollIntoViewOptions = { behavior: 'smooth', inline: 'nearest', block: 'nearest' };

  const handleOnChange = ({ realIndex }: { realIndex: number }) => {
    setCurrentIndex(realIndex);
    navRef.current?.children[realIndex].scrollIntoView(scrollBehavior);
  };

  const handleOnClickNavigation = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>, index: number) => {
    swipperApi.get().slideTo(index);
    currentTarget.scrollIntoView(scrollBehavior);
  };

  const handleExpandButtonClick = () => {
    setCurrentIndexModal(currentIndex);
    setModalOpen(true);
  };

  return (
    <>
      <Swiper
        className={styles.carousel}
        onSlideChange={handleOnChange}
        resizeObserver
        ref={apiRef}
        {...swiperProps}
      >
        {items.map((media, index) => (
          <SwiperSlide key={index}>
            <SubjectMedia entry={media} className={styles.media} playWhenInView loop />
          </SwiperSlide>
        ))}
        {enableModal && (
          <button
            className={styles.expandButton}
            aria-label={t('carousel.expand-image')}
            onClick={() => handleExpandButtonClick()}
          >
            <Image src={expandIcon} />
          </button>
        )}
      </Swiper>
      <ul className={styles.navigation} ref={navRef}>
        {items.map((media, index) => (
          <li key={index}>
            <button
              className={cx(styles.navigationButton, index === currentIndex && styles.active)}
              onClick={e => handleOnClickNavigation(e, index)}
              onFocus={({ currentTarget }) => currentTarget.scrollIntoView(scrollBehavior)}
            >
              <SubjectMedia entry={media} className={styles.media} playWhenInView loop />
            </button>
          </li>
        ))}
      </ul>
      <Modal
        shouldCloseOnEsc
        className={styles.modal}
        overlayClassName={styles.modalOverlay}
        isOpen={modalOpen}
        onRequestClose={() => setModalOpen(false)}
      >
        <button
          className={styles.closeModalButton}
          aria-label={t('close')}
          onClick={() => setModalOpen(false)}
        >
          <Image src={closeIcon} />
        </button>
        <SubjectMedia entry={items[currentIndexModal]} className={styles.media} playWhenInView loop />
        <nav className={styles.modalNav}>
          <CircleArrowButton
            variant="left"
            className={styles.modalNavigationButton}
            onClick={() => setCurrentIndexModal(currentIndexModal - 1)}
            aria-label={t('previous')}
            disabled={currentIndexModal === 0}
          />
          <span className={styles.counter}>{`${currentIndexModal + 1}/${items.length}`}</span>
          <CircleArrowButton
            variant="right"
            className={styles.modalNavigationButton}
            aria-label={t('next')}
            onClick={() => setCurrentIndexModal(currentIndexModal + 1)}
            disabled={currentIndexModal === items.length - 1}
          />
        </nav>
      </Modal>
    </>
  );
};

export default InlineGalleryModule;
